import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { MessageCircle, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useStores } from '@/components/StoreProvider'

type ChatConsentLauncherProps = {
  /** Called after the visitor allows functional cookies from here, so the chat can open straight away */
  onEnable: () => void
}

/**
 * Stands in for the Intercom launcher while functional cookies are declined, so support stays
 * reachable. It loads nothing from Intercom until the visitor enables the chat.
 */
export const ChatConsentLauncher = observer(({ onEnable }: ChatConsentLauncherProps) => {
  const { t } = useTranslation()
  const { consentStore } = useStores()
  const [open, setOpen] = useState(false)

  // Before the first choice the banner is the prompt; once chat is allowed Intercom draws its own launcher
  if (!consentStore.decided || consentStore.functional) return null

  return (
    <div className="fixed bottom-5 right-5 z-[900] flex flex-col items-end gap-3">
      {open && (
        <div role="dialog" aria-label={t('consent.chat.title')} className="w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-xl">
          <p className="text-sm font-semibold text-gray-900">{t('consent.chat.title')}</p>
          <p className="mt-1 text-sm leading-relaxed text-gray-600">{t('consent.chat.text')}</p>
          <div className="mt-3 flex flex-col gap-2">
            <Button
              variant="default"
              onClick={() => {
                setOpen(false)
                onEnable()
                consentStore.allow('functional')
              }}
            >
              {t('consent.chat.enable')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setOpen(false)
                consentStore.openPreferences()
              }}
            >
              {t('consent.cookie_preferences')}
            </Button>
          </div>
        </div>
      )}
      <button
        type="button"
        aria-label={open ? t('consent.close') : t('consent.chat.launcher')}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex size-12 cursor-pointer items-center justify-center rounded-full bg-[var(--color-primary)] text-white shadow-lg transition-transform hover:scale-105 focus-visible:ring-[3px] focus-visible:ring-[var(--color-primary)]/40 focus-visible:outline-none"
      >
        {open ? <X className="size-5" /> : <MessageCircle className="size-5" />}
      </button>
    </div>
  )
})
ChatConsentLauncher.displayName = 'ChatConsentLauncher'
